import { AxiosInstance } from "axios";
import { IRegistrationDelegate } from "../interfaces/IRegistrationDelegate";
import { IEventList } from "../interfaces/IEventList";
import { IEventDay } from "../interfaces/IEventDay";
import { IEventSession } from "../interfaces/IEventSession";
import { ISessionDelegate } from "../interfaces/ISessionDelegate";
import { IDelegate } from "../interfaces/IDelegate";
import { IDelegateRole } from "../interfaces/IDelegateRole";
import { ILocationList } from "../interfaces/ILocationList";
import { IEventSessionType } from "../interfaces/IEventSessionType";
import { ITicket } from "../interfaces/ITicket";
import { IValidateVoucher } from "../interfaces/IValidateVoucher";
import { IVoucherState } from "../interfaces/IVoucherState";
import { buildPagedFilterQuery } from "../utils/queryHelpers";
import { IPagedFilterRequest } from "../interfaces/IPagedFilterResults";
import { ITaskComment } from "../interfaces/ITasks";
import { IUserProfileResponse } from "../interfaces/IUserProfile";
import { IPagedFilterResponse } from "../interfaces/IPagedFilterResponse";
import { ReCaptchaTokenResponse } from "../interfaces/IReCaptchaTokenResponse ";
import { IContactUsRequest } from "../interfaces/IContactUsRequest";
import { IContactUsResponse } from "../interfaces/IContactUsResponse";
import { IPublicEventDetails } from "../interfaces/IPublicEventDetails";
import { IPublicEventRegistration } from "../interfaces/IPublicEventRegistration";
import { ITicketBundle } from "../interfaces/ITicketBundle";
import { IEventOrganiser } from "../interfaces/IEventOrganiser";
import { IPublicOrganiserEventFeedItem, IPublicOrganiserProfile } from "../interfaces/IPublicOrganiserProfile";
import { IPublicOrganiserCommunitiesResponse, OrganiserCommunityRelationship } from "../interfaces/IPublicOrganiserCommunity";
import { IOrganiserFollow } from "../interfaces/IOrganiserFollow";
import { IOrganiserDiscovery } from "../interfaces/IOrganiserDiscovery";

export class PublicEventService {

    static updateTask(arg0: { status: number; id: string; title: string; description: string; assignedTo: string; dueDate: Date; eventId: string; comments: ITaskComment[]; createdAt: Date; }) {
        throw new Error('Method not implemented.');
    }
    private apiClient: AxiosInstance;
    private endpoint: string;
    private preview: boolean;

    constructor(apiClient: AxiosInstance, endpoint: string = 'event', preview: boolean = false) {
        this.apiClient = apiClient;
        this.endpoint = endpoint;
        this.preview = preview;
    }

    private buildApiAssetUrl(path: string): string {
        const baseUrl = String(this.apiClient.defaults.baseURL ?? '').replace(/\/+$/, '');
        const normalizedPath = path.replace(/^\/+/, '');
        return `${baseUrl}/${normalizedPath}`;
    }

    async getAll(request: IPagedFilterRequest, options?: { signal?: AbortSignal; includeOrganiser?: boolean }): Promise<IPagedFilterResponse<IEventList>> {
        try {
            const query = buildPagedFilterQuery(request);
            const url = `${this.endpoint}?${query}${options?.includeOrganiser ? '&includeOrganiser=true' : ''}`;
            const response = options?.signal
                ? await this.apiClient.get(url, { signal: options.signal })
                : await this.apiClient.get(url);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    /** The signed-in participant's own events (registered/organised) — supports the same paging/sort/filter query as `getAll`. */
    async getRoleBasedEvents(request: IPagedFilterRequest): Promise<IPagedFilterResponse<IEventList>> {
        try {
            const query = buildPagedFilterQuery(request);
            const response = await this.apiClient.get(`/participant/${this.endpoint}?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getWaitListEvents(): Promise<IEventList[]> {
        try {
            const response = await this.apiClient.get(`/participant/${this.endpoint}/waitlists`);
            const data = response.data;
            return Array.isArray(data) ? data : (data?.items ?? []);
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getFavouriteEvents(): Promise<IEventList[]> {
        try {
            const response = await this.apiClient.get(`/participant/${this.endpoint}/favourites`);
            const data = response.data;
            return Array.isArray(data) ? data : (data?.items ?? []);
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while fetching favourites');
        }
    }

    async verifyRecaptchaV3(token: string): Promise<ReCaptchaTokenResponse> {
        const res = await this.apiClient.post("/verification/recaptcha/v3", { token });
        return res.data;
    }

    async verifyRecaptchaV2(Token: string): Promise<ReCaptchaTokenResponse> {
        const res = await this.apiClient.post("/verification/recaptcha/v2", { Token });
        return res.data;
    }

    async SubmitContactUs(request: IContactUsRequest): Promise<IContactUsResponse> {
        const res = await this.apiClient.post("/contact/us", request);
        return res.data;
    }

    async getOrganiser(eventId: string): Promise<IEventOrganiser> {
        const response = await this.apiClient.get(`/event/${eventId}/organiser`, {
            params: this.preview ? { preview: true } : undefined,
        });
        const data = response.data;
        return {
            companyId: data.companyId ?? data.CompanyId,
            companyName: data.companyName ?? data.CompanyName,
            companyKey: data.companyKey ?? data.CompanyKey,
            logoUrl: data.logoUrl ?? data.LogoUrl ?? data.companyLogoUrl ?? data.CompanyLogoUrl,
        };
    }

    async getOrganiserProfile(companyKey: string, take: number = 6): Promise<IPublicOrganiserProfile> {
        const response = await this.apiClient.get(`/organiser/${encodeURIComponent(companyKey)}`, {
            params: { take }
        });
        return response.data;
    }

    async getOrganiserEvents(
        companyKey: string,
        timeframe: 'Upcoming' | 'Past',
        page: number = 1,
        pageSize: number = 8,
    ): Promise<IPagedFilterResponse<IPublicOrganiserEventFeedItem>> {
        const response = await this.apiClient.get(`/organiser/${encodeURIComponent(companyKey)}/events`, {
            params: { timeframe, page, pageSize }
        });
        return response.data;
    }

    async getOrganiserCommunities(
        companyKey: string,
        options?: {
            relationship?: 'All' | OrganiserCommunityRelationship;
            page?: number;
            pageSize?: number;
            search?: string;
        }
    ): Promise<IPublicOrganiserCommunitiesResponse> {
        const response = await this.apiClient.get(`/organiser/${encodeURIComponent(companyKey)}/communities`, {
            params: {
                relationship: options?.relationship ?? 'All',
                page: options?.page ?? 1,
                pageSize: options?.pageSize ?? 12,
                search: options?.search || undefined,
            },
        });
        return response.data;
    }

    async contactOrganiser(eventId: string, request: IContactUsRequest): Promise<IContactUsResponse> {
        const response = await this.apiClient.post(`/events/${eventId}/contact-organiser`, request);
        return response.data;
    }

    async addToFavourites(eventId: string): Promise<void> {
        try {
            await this.apiClient.post(`/participant/${this.endpoint}/favourites`, eventId, {
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }
    async removeFromFavourites(eventId: string): Promise<void> {
        try {
            await this.apiClient.delete(`/participant/${this.endpoint}/favourites/${eventId}`);
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred');
        }
    }

    async get(id: string): Promise<IPublicEventDetails> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}`, {
                params: {
                    t: Date.now(), // Cache-busting param
                    ...(this.preview ? { preview: true } : {}),
                },
            });
            return response.data;
        } catch (error: any) {
            const err: any = new Error(error.response ? error.response.data : 'An error occurred');
            err.status = error.response?.status;
            throw err;
        }
    }

    async getRelatedEventsByOrganiser(id: string, take: number = 8): Promise<IEventList[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/related-events`, {
                params: { take, ...(this.preview ? { preview: true } : {}) }
            });
            return response.data;
        } catch (error: any) {
            const err: any = new Error(error.response ? error.response.data : 'An error occurred');
            err.status = error.response?.status;
            throw err;
        }
    }
    async getRegistration(id: string, roleId?: string, claimTicketId?: string): Promise<IPublicEventRegistration> {
        try {
            const params: any = {
                t: Date.now(), // Cache-busting param
            };

            if (roleId) {
                params.roleId = roleId;
            }

            if (claimTicketId) {
                params.claimTicketId = claimTicketId;
            }

            const response = await this.apiClient.get(`${this.endpoint}/${id}/registration-details`, {
                params,
            });
            return response.data;
        } catch (error: any) {
            const err: any = new Error(error.response ? error.response.data : 'An error occurred');
            err.status = error.response?.status;
            throw err;
        }
    }
    async getDays(id: string): Promise<IEventDay[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/day`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getSessions(id: string, dayId: string): Promise<IEventSession[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/day/${dayId}/sessions`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getSessionDelegates(id: string, dayId: string, sessionId: string): Promise<ISessionDelegate[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/day/${dayId}/sessions/${sessionId}/delegates`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getLocations(id: string): Promise<ILocationList[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/locations`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getTypes(id: string): Promise<IEventSessionType[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/session-types`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getDelegates(id: string): Promise<IDelegate[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/delegates`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getDelegateRoles(id: string | undefined): Promise<IDelegateRole[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/delegate-roles`, {
                params: this.preview ? { preview: true } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An errorOccurred');
        }
    }

    async getEventAttendees(id: string, request: IPagedFilterRequest): Promise<IPagedFilterResponse<IDelegate>> {
        try {
            const query = buildPagedFilterQuery(request);
            const response = await this.apiClient.get(`${this.endpoint}/${id}/attendees?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getTickets(id: string | undefined): Promise<ITicket[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/tickets`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getBundles(id: string | undefined): Promise<ITicketBundle[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/bundles`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async validateVoucher(id: string | undefined, voucher: IValidateVoucher): Promise<IVoucherState> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${id}/validate-voucher`, voucher);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getImage(id: string): Promise<string> {
        const imagePath = `${this.endpoint}/${id}/image`;
        const assetUrl = this.buildApiAssetUrl(this.preview ? `${imagePath}?preview=true` : imagePath);

        try {
            const response = await this.apiClient.get<Blob>(assetUrl, {
                responseType: 'blob'
            });

            return URL.createObjectURL(response.data);
        } catch {
            return assetUrl;
        }
    }

    async downloadCalendar(id: string): Promise<{ blob: Blob; fileName: string }> {
        try {
            const calUrl = `${window.location.origin}/cal/${id}/calendar.ics`;
            const response = await this.apiClient.get(calUrl, {
                responseType: 'blob'
            });

            const disposition = response.headers?.['content-disposition'] as string | undefined;
            let fileName = `event-${id}.ics`;

            if (disposition) {
                const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (match?.[1]) {
                    fileName = match[1].replace(/['"]/g, '');
                }
            }

            return { blob: response.data, fileName };
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to download calendar');
        }
    }

    async verifyRecaptcha(token: string): Promise<{ success: boolean; error?: string }> {
        const response = await this.apiClient.post('/verify-recaptcha', { token });
        return response.data;
    }


    async register(id: string, registration: IRegistrationDelegate): Promise<{ confirmed: boolean; paymentNeeded: boolean; paymentKey: string; stripeAccountId?: string; currency?: string; requiresApproval: boolean; transactionId?: string }> {
        const response = await this.apiClient.post(`${this.endpoint}/${id}/register`, registration);
        return response.data;
    }

    /**
     * Tells the API the attendee has abandoned or failed checkout so held tickets are released.
     * Uses a keepalive request so the call still completes when the tab is closing — a plain XHR
     * fired from pagehide/beforeunload is dropped by most mobile browsers.
     */
    async markPaymentFailed(eventId: string, transactionId: string): Promise<void> {
        const path = `${this.endpoint}/${eventId}/payment-failed`;

        if (typeof fetch !== 'function') {
            await this.apiClient.post(path, { transactionId });
            return;
        }

        const response = await fetch(this.buildApiAssetUrl(path), {
            method: 'POST',
            keepalive: true,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-Client-Platform': 'web',
            },
            body: JSON.stringify({ transactionId }),
        });

        if (!response.ok) {
            throw new Error(`Payment cleanup failed with status ${response.status}`);
        }
    }

    async addTicketsToMyRegistration(
        id: string,
        request: {
            selectedTickets: Array<{ ticketId: string; quantity: number; chosenAmount?: number }>;
            selectedBundleId?: string;
            selectedBundleQuantity?: number;
            voucherCode?: string;
            currency?: string;
        }
    ): Promise<{ confirmed: boolean; paymentNeeded: boolean; paymentKey: string; stripeAccountId?: string; currency?: string; message: string }> {
        const response = await this.apiClient.post(`${this.endpoint}/${id}/add-tickets`, request);
        return response.data;
    }

    async getTopTags(): Promise<string[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/top-tags`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getDelegateProfile(userId: string, eventId: string, roleId: string): Promise<IUserProfileResponse> {
        try {
            const response = await this.apiClient.get(
                `/event/${eventId}/delegates/${userId}`,
                { params: { roleId } }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getAttendeeProfile(userId: string, eventId: string): Promise<IUserProfileResponse> {
        try {
            const response = await this.apiClient.get(
                `/event/${eventId}/attendees/${userId}`
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }


    /**
     * Track a view of an event page (public endpoint - no auth required)
     */
    async trackEventView(
        eventId: string,
        sessionId?: string,
        userAgent?: string,
        referrer?: string,
        viewSource?: string,
        utmSource?: string,
        utmMedium?: string,
        utmCampaign?: string
    ): Promise<void> {
        try {
            await this.apiClient.post(`event/${eventId}/track-view`, {
                sessionId,
                userAgent,
                referrer,
                viewSource,
                utmSource,
                utmMedium,
                utmCampaign,
            });
        } catch (error: any) {
            // Silently fail - don't break user experience
            console.warn('Failed to track event view:', error);
        }
    }

    async trackOrganiserProfileInteraction(
        companyKey: string,
        payload: {
            interactionType: string;
            interactionLabel?: string;
            relatedEventId?: string;
            sessionId?: string;
            userAgent?: string;
            referrer?: string;
            utmSource?: string;
            utmMedium?: string;
            utmCampaign?: string;
        }
    ): Promise<void> {
        try {
            await this.apiClient.post(`/organiser/${companyKey}/track-interaction`, payload);
        } catch (error: any) {
            console.warn('Failed to track organiser profile interaction:', error);
        }
    }

    async followOrganiser(companyId: string, source?: string): Promise<void> {
        try {
            const params = source ? { source } : {};
            await this.apiClient.post(`/participant/organiser/${companyId}/follow`, {}, { params });
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to follow organiser');
        }
    }

    async unfollowOrganiser(companyId: string): Promise<void> {
        try {
            await this.apiClient.delete(`/participant/organiser/${companyId}/follow`);
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to unfollow organiser');
        }
    }

    async getFollowedOrganisers(request: IPagedFilterRequest): Promise<IPagedFilterResponse<IOrganiserFollow>> {
        try {
            const query = buildPagedFilterQuery(request);
            const response = await this.apiClient.get(`/participant/organiser/follows?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to fetch followed organisers');
        }
    }

    async isFollowing(companyId: string): Promise<boolean> {
        try {
            const result = await this.apiClient.post(`/participant/organiser/follow-states`, [companyId]);
            const states = result.data as Record<string, boolean>;
            return states[companyId] ?? false;
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to check follow state');
        }
    }

    async getFollowStates(companyIds: string[]): Promise<Record<string, boolean>> {
        try {
            if (!companyIds || companyIds.length === 0) {
                return {};
            }
            const response = await this.apiClient.post(`/participant/organiser/follow-states`, companyIds);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to fetch follow states');
        }
    }

    async discoverOrganisers(request: IPagedFilterRequest, signal?: AbortSignal): Promise<IPagedFilterResponse<IOrganiserDiscovery>> {
        try {
            const query = buildPagedFilterQuery(request);
            const url = `/participant/organiser/discover?${query}`;
            const response = signal
                ? await this.apiClient.get(url, { signal })
                : await this.apiClient.get(url);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to discover organisers');
        }
    }
}
